import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Tag,
  Space,
  Typography,
  Row,
  Col,
  Collapse,
  Spin,
  Tooltip,
} from 'antd';
import type { TableProps } from 'antd';
import {
  SafetyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CrownOutlined,
  TeamOutlined,
  AppstoreOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { http } from '../../services/request';
import { COLORS } from '../../constants/colors';

const { Title, Text } = Typography;

// 角色配置
const roleConfig: Record<string, { 
  color: string; 
  text: string; 
  icon: React.ReactNode;
  description: string;
}> = {
  owner: { 
    color: 'purple', 
    text: '所有者', 
    icon: <CrownOutlined />,
    description: '拥有系统所有权限，不可修改'
  },
  admin: { 
    color: 'blue', 
    text: '管理员', 
    icon: <SafetyOutlined />,
    description: '除转让所有权外所有权限'
  },
  operator: { 
    color: 'green', 
    text: '运营', 
    icon: <TeamOutlined />,
    description: '提案、星探、邀约的操作权限'
  },
  viewer: { 
    color: 'default', 
    text: '查看者', 
    icon: <EyeOutlined />,
    description: '仅查看权限，不可操作'
  },
};

// 权限分组定义
const permissionGroups = [
  {
    key: 'organization',
    name: '组织管理',
    permissions: [
      { key: 'tenant:view', name: '查看租户信息' },
      { key: 'tenant:edit', name: '编辑租户信息' },
      { key: 'tenant:transfer', name: '转让所有权' },
      { key: 'members:view', name: '查看成员列表' },
      { key: 'members:invite', name: '邀请成员' },
      { key: 'members:manage', name: '管理成员角色' },
      { key: 'roles:view', name: '查看角色权限' },
    ],
  },
  {
    key: 'proposal',
    name: '提案中心',
    permissions: [
      { key: 'proposal:view', name: '查看提案' },
      { key: 'proposal:create', name: '创建提案' },
      { key: 'proposal:edit', name: '编辑提案' },
      { key: 'proposal:delete', name: '删除提案' },
      { key: 'proposal:generate', name: 'AI生成提案' },
    ],
  },
  {
    key: 'scout',
    name: '红人星探',
    permissions: [
      { key: 'scout:view', name: '查看红人信息' },
      { key: 'scout:search', name: '搜索红人' },
      { key: 'scout:create_task', name: '创建筛选任务' },
      { key: 'scout:export', name: '导出红人数据' },
    ],
  },
  {
    key: 'outreach',
    name: '红人邀约',
    permissions: [
      { key: 'outreach:view', name: '查看活动' },
      { key: 'outreach:create', name: '创建活动' },
      { key: 'outreach:edit', name: '编辑活动' },
      { key: 'outreach:delete', name: '删除活动' },
      { key: 'outreach:send', name: '发送邀约' },
      { key: 'outreach:monitor', name: '监控回复' },
    ],
  },
  {
    key: 'knowledge',
    name: '知识库',
    permissions: [
      { key: 'knowledge:view', name: '查看文档' },
      { key: 'knowledge:upload', name: '上传文档' },
      { key: 'knowledge:delete', name: '删除文档' },
      { key: 'knowledge:query', name: '查询知识库' },
    ],
  },
  {
    key: 'system',
    name: '系统设置',
    permissions: [
      { key: 'settings:view', name: '查看设置' },
      { key: 'subscription:view', name: '查看订阅' },
      { key: 'subscription:manage', name: '管理订阅' },
      { key: 'billing:view', name: '查看账单' },
    ],
  },
];

// 角色权限映射（系统预设）
const rolePermissionMap: Record<string, string[]> = {
  owner: [
    'tenant:view', 'tenant:edit', 'tenant:transfer',
    'members:view', 'members:invite', 'members:manage',
    'roles:view',
    'proposal:view', 'proposal:create', 'proposal:edit', 'proposal:delete', 'proposal:generate',
    'scout:view', 'scout:search', 'scout:create_task', 'scout:export',
    'outreach:view', 'outreach:create', 'outreach:edit', 'outreach:delete', 'outreach:send', 'outreach:monitor',
    'knowledge:view', 'knowledge:upload', 'knowledge:delete', 'knowledge:query',
    'settings:view', 'subscription:view', 'subscription:manage', 'billing:view',
  ],
  admin: [
    'tenant:view', 'tenant:edit',
    'members:view', 'members:invite', 'members:manage',
    'roles:view',
    'proposal:view', 'proposal:create', 'proposal:edit', 'proposal:delete', 'proposal:generate',
    'scout:view', 'scout:search', 'scout:create_task', 'scout:export',
    'outreach:view', 'outreach:create', 'outreach:edit', 'outreach:delete', 'outreach:send', 'outreach:monitor',
    'knowledge:view', 'knowledge:upload', 'knowledge:delete', 'knowledge:query',
    'settings:view', 'subscription:view', 'billing:view',
  ],
  operator: [
    'proposal:view', 'proposal:create', 'proposal:edit', 'proposal:generate',
    'scout:view', 'scout:search', 'scout:create_task',
    'outreach:view', 'outreach:create', 'outreach:edit', 'outreach:send', 'outreach:monitor',
    'knowledge:view', 'knowledge:upload', 'knowledge:query',
  ],
  viewer: [
    'proposal:view',
    'scout:view',
    'outreach:view',
    'knowledge:view',
  ],
};

// 接口类型定义
interface RoleInfo {
  name: string;
  display_name: string;
  description: string;
  is_system: boolean;
  member_count: number;
}

interface PermissionInfo {
  code: string;
  name: string;
  module: string;
}

export const RoleManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState<RoleInfo[]>([]);
  const [rolePermissions, setRolePermissions] = useState<Record<string, PermissionInfo[]>>({});
  const [activeRole, setActiveRole] = useState<string>('owner');

  // 获取角色列表
  useEffect(() => {
    const fetchRoles = async () => {
      setLoading(true);
      try {
        const response = await http.get<RoleInfo[]>('/api/v1/tenant/roles');
        setRoles(response.data || []);
        // 如果API返回成功，再获取每个角色的权限
        if (response.data && response.data.length > 0) {
          fetchAllRolePermissions(response.data.map(r => r.name));
        }
      } catch {
        // API未实现时使用默认数据
        setRoles([
          { name: 'owner', display_name: '所有者', description: '拥有系统所有权限，不可修改', is_system: true, member_count: 1 },
          { name: 'admin', display_name: '管理员', description: '除转让所有权外所有权限', is_system: true, member_count: 0 },
          { name: 'operator', display_name: '运营', description: '提案、星探、邀约的操作权限', is_system: true, member_count: 0 },
          { name: 'viewer', display_name: '查看者', description: '仅查看权限，不可操作', is_system: true, member_count: 0 },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchRoles();
  }, []);

  // 获取所有角色的权限
  const fetchAllRolePermissions = async (roleNames: string[]) => {
    const permissions: Record<string, PermissionInfo[]> = {};
    
    for (const roleName of roleNames) {
      try {
        const response = await http.get<PermissionInfo[]>(`/api/v1/tenant/roles/${roleName}/permissions`);
        permissions[roleName] = response.data || [];
      } catch {
        // API未实现时使用预设权限
        permissions[roleName] = (rolePermissionMap[roleName] || []).map(code => {
          const group = permissionGroups.find(g => g.permissions.some(p => p.key === code));
          const perm = group?.permissions.find(p => p.key === code);
          return {
            code,
            name: perm?.name || code,
            module: group?.name || '其他',
          };
        });
      }
    }
    
    setRolePermissions(permissions);
  };

  // 获取角色当前权限（优先从API获取，否则使用预设）
  const getRolePermissions = (roleName: string): string[] => {
    if (rolePermissions[roleName] && rolePermissions[roleName].length > 0) {
      return rolePermissions[roleName].map(p => p.code);
    }
    return rolePermissionMap[roleName] || [];
  };

  // 检查角色是否有某个权限
  const hasPermission = (roleName: string, permissionKey: string): boolean => {
    const permissions = getRolePermissions(roleName);
    return permissions.includes(permissionKey);
  };

  // 权限矩阵表格列定义
  const getPermissionColumns = () => {
    const columns: TableProps<{ key: string; name: string }>['columns'] = [
      {
        title: '权限',
        dataIndex: 'name',
        key: 'name',
        width: 180,
        render: (text: string) => <Text>{text}</Text>,
      },
    ];

    // 添加每个角色的列
    Object.keys(roleConfig).forEach(roleKey => {
      columns.push({
        title: (
          <Space>
            {roleConfig[roleKey].icon}
            <span>{roleConfig[roleKey].text}</span>
          </Space>
        ) as React.ReactNode,
        key: roleKey,
        width: 100,
        align: 'center' as const,
        render: (_: unknown, record: { key: string; name: string }) => {
          const has = hasPermission(roleKey, record.key);
          return has ? (
            <CheckCircleOutlined style={{ color: COLORS.success, fontSize: 16 }} />
          ) : (
            <CloseCircleOutlined style={{ color: COLORS.borderLight, fontSize: 16 }} />
          );
        },
      });
    });

    return columns;
  };



  // 角色卡片
  const renderRoleCard = (roleKey: string) => {
    const config = roleConfig[roleKey];
    const roleInfo = roles.find(r => r.name === roleKey);
    const memberCount = roleInfo?.member_count || 0;

    return (
      <Card
        key={roleKey}
        hoverable
        style={{
          borderLeft: `3px solid ${config.color === 'purple' ? COLORS.purple : config.color === 'blue' ? COLORS.primary : config.color === 'green' ? COLORS.success : COLORS.borderLight}`,
        }}
        onClick={() => setActiveRole(roleKey)}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space>
            {config.icon}
            <Text strong style={{ fontSize: 16 }}>{config.text}</Text>
            <Tag color={config.color}>系统角色</Tag>
          </Space>
          <Text type="secondary">{config.description}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            成员数：{memberCount} 人
          </Text>
        </Space>
      </Card>
    );
  };

  // 角色权限详情
  const renderRoleDetail = () => {
    const config = roleConfig[activeRole];
    const permissions = getRolePermissions(activeRole);
    
    // 按分组统计权限
    const groupedPermissions = permissionGroups.map(group => ({
      group: group.name,
      total: group.permissions.length,
      has: group.permissions.filter(p => permissions.includes(p.key)).length,
      permissions: group.permissions.map(p => ({
        ...p,
        has: permissions.includes(p.key),
      })),
    }));

    return (
      <Card 
        title={
          <Space>
            {config.icon}
            <span>{config.text} - 权限详情</span>
          </Space>
        }
      >
        {groupedPermissions.map(group => (
          <div key={group.group} style={{ marginBottom: 16 }}>
            <Text strong style={{ marginBottom: 8, display: 'block' }}>
              {group.group} ({group.has}/{group.total})
            </Text>
            <Row gutter={[8, 8]}>
              {group.permissions.map(perm => (
                <Col span={12} key={perm.key}>
                  <Tooltip title={perm.key}>
                    <Space>
                      {perm.has ? (
                        <CheckCircleOutlined style={{ color: COLORS.success }} />
                      ) : (
                        <CloseCircleOutlined style={{ color: COLORS.borderLight }} />
                      )}
                      <Text type={perm.has ? undefined : 'secondary'}>{perm.name}</Text>
                    </Space>
                  </Tooltip>
                </Col>
              ))}
            </Row>
          </div>
        ))}
      </Card>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>
        角色权限
      </Title>

      <Row gutter={[24, 24]}>
        {/* 左侧：角色列表 */}
        <Col span={8}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {Object.keys(roleConfig).map(roleKey => renderRoleCard(roleKey))}
          </Space>
        </Col>

        {/* 右侧：权限详情 */}
        <Col span={16}>
          {renderRoleDetail()}
        </Col>
      </Row>

      {/* 权限矩阵 */}
      <Card 
        title="权限矩阵" 
        style={{ marginTop: 24 }}
        extra={
          <Text type="secondary">
            <CheckCircleOutlined style={{ color: COLORS.success, marginRight: 4 }} />
            表示拥有该权限
          </Text>
        }
      >
        <Collapse
          defaultActiveKey={['organization']}
        >
          {permissionGroups.map(group => (
            <Collapse.Panel
              key={group.key}
              header={
                <Space>
                  <AppstoreOutlined />
                  <span>{group.name}</span>
                  <Tag>{group.permissions.length} 项权限</Tag>
                </Space>
              }
            >
              <Table
                size="small"
                columns={getPermissionColumns()}
                dataSource={group.permissions.map(p => ({ key: p.key, name: p.name }))}
                rowKey="key"
                pagination={false}
                bordered
              />
            </Collapse.Panel>
          ))}
        </Collapse>
      </Card>
    </div>
  );
};
